'use client'

import { Accordion, AccordionItem } from '@lastbrand/web-ui/accordion'
import { css, cx } from '@lastbrand/web-ui-styled-system/css'
import { useCallback, useRef, useState } from 'react'

import { TEST_LOCATORS } from '@src/constants/test-locators'
import { SectionContentContainer } from '@src/ds/blocks/section-content-container'
import type { FAQSectionProps } from '@src/quince-components/src'
import type { AccordianItemProps } from '@src/quince-components/src/atoms/accordianItem/AccordianItem'
import { RichText as RichTextC, type RichTextProps } from '@src/quince-components/src/molecules'
import RichText from '@src/quince-site-common/src/domains/RichText'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import type { Node, Document } from '@contentful/rich-text-types'

/* eslint-disable i18next/no-literal-string --
 * Panda design tokens in css(), style props, and Accordion in this module.
 */
// Background + vertical rhythm for the FAQ band. `SectionContentContainer` owns the
// shared content frame (`px` gutter + `max-w: 1504` centered).
const sectionBandClass = css({
  backgroundColor: 'offwhite',
  paddingBlock: { base: 'space-48', lg: 'space-100' },
})

const headerTextClass = css({
  textAlign: 'center',
  width: '100%',
  textTransform: 'uppercase',
  color: 'black',
  marginBottom: { base: 'space-40', lg: 'space-80' },
  textStyle: { base: 'title-20', lg: 'title-24' },
})

const faqHeaderStrong = css({
  '& p': { fontWeight: 500 },
})

const accordionRowClass = css({ maxWidth: { base: '100%', lg: '944px' }, marginInline: 'auto' })

const accordionBodyClass = css({
  color: 'black',
  '& a': { color: 'black' },
  textStyle: 'body-14',
})

const accordionWidthCss = { width: '100%' }

/* eslint-enable i18next/no-literal-string */

const plainTextFromNode = (node: Node): string => {
  if (node.nodeType === 'text' && 'value' in node) {
    return (node as { value: string }).value
  }
  if ('content' in node && Array.isArray((node as { content: Node[] }).content)) {
    return (node as { content: Node[] }).content.map(plainTextFromNode).join(' ')
  }
  return ''
}

const plainTextFromDocument = (doc: Document | undefined | null): string => {
  if (!doc) return ''
  return doc.content
    .map(n => plainTextFromNode(n as Node))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const getItemValue = (item: AccordianItemProps, index: number) =>
  item.key != null ? String(item.key) : `item-${index}`

const getDescriptionForTrack = (item: AccordianItemProps): string | undefined => {
  const firstNode = item.header?.raw.content[0]?.content[0]
  return firstNode?.nodeType === 'text' ? firstNode.value : undefined
}

const FaqDs = ({
  title = '',
  header = null,
  accordianData = { data: [] },
  className = '',
  boldHeader = false,
  showFirstQuestionExpanded = false,
  productProperties,
  initialExpandedIndices,
  onItemToggle,
  // usePdpStyling is intentionally not implemented in the DS variant.
  // Do not flip the DS gate for PDP callers until V2 visual parity is ported to a Panda variant.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  usePdpStyling: _usePdpStyling,
}: FAQSectionProps) => {
  const { data: faqRows = [] } = accordianData
  const data: AccordianItemProps[] = faqRows

  const [openValues, setOpenValues] = useState<string[]>(() => {
    if (initialExpandedIndices != null) {
      return initialExpandedIndices
        .filter(i => i >= 0 && i < data.length)
        .map(i => {
          const item = data[i]
          return item ? getItemValue(item, i) : `item-${i}`
        })
    }
    if (showFirstQuestionExpanded) {
      const first = data[0]
      if (first) return [getItemValue(first, 0)]
    }
    return []
  })
  const prevValueRef = useRef<string[]>(openValues)

  const sendFAQViewedEvent = useCallback(() => {
    trackEvent(TELEMETRY_EVENT_NAMES.FAQ_VIEWED, {
      no_of_FAQ_cards: data.length,
    })
  }, [data.length])

  const { ref: isFAQVisible } = useTrackIsElementVisible(() => null, sendFAQViewedEvent)

  const onAccordionChange = useCallback(
    (next: string[]) => {
      const prev = prevValueRef.current

      data.forEach((item, index) => {
        const val = getItemValue(item, index)
        const wasOpen = prev.includes(val)
        const isOpen = next.includes(val)
        if (wasOpen !== isOpen && isOpen) {
          onItemToggle?.(index, isOpen)
          const desc = getDescriptionForTrack(item) ?? (item.key != null ? String(item.key) : undefined)
          trackEvent(TELEMETRY_EVENT_NAMES.FAQ_CLICKED, {
            FAQ_description: desc,
            productProperties,
          })
        }
      })

      prevValueRef.current = next
      setOpenValues(next)
    },
    [data, onItemToggle, productProperties],
  )

  const headerRichTextProps: RichTextProps | undefined = RichText.makeRichTextProps(header)

  return (
    <SectionContentContainer
      ref={isFAQVisible}
      id={title ? title.toLowerCase() : undefined}
      className={cx(sectionBandClass, className)}
      data-faq-section
      data-testid={TEST_LOCATORS.PDP_SECTIONS.FAQ}
    >
      <div data-testid="faq-header" className={cx(boldHeader && faqHeaderStrong)}>
        {header && headerRichTextProps && <RichTextC {...headerRichTextProps} className={headerTextClass} />}
      </div>
      <div className={accordionRowClass}>
        <Accordion
          type="multiple"
          value={openValues}
          onValueChange={onAccordionChange}
          // eslint-disable-next-line i18next/no-literal-string -- Radix variant prop
          variant="decorative"
          css={accordionWidthCss}
        >
          {data.map((row, index) => {
            const value = getItemValue(row, index)
            const titleText = plainTextFromDocument(row.header?.raw)
            if (!titleText) return null
            const bodyProps: RichTextProps | undefined = RichText.makeRichTextProps(row.body)

            return (
              <AccordionItem
                // eslint-disable-next-line i18next/no-literal-string -- CSS keyword, not user-facing copy
                headerCss={{ textAlign: 'left', _hover: { color: 'darkgray' }, _active: { color: 'darkgray' } }}
                key={value}
                value={value}
                title={titleText}
                // eslint-disable-next-line i18next/no-literal-string -- CSS keyword, not user-facing copy
                contentWrapperCss={{ color: 'black', '& a': { color: 'black' } }}
              >
                {bodyProps ? <RichTextC {...bodyProps} className={accordionBodyClass} /> : null}
              </AccordionItem>
            )
          })}
        </Accordion>
      </div>
    </SectionContentContainer>
  )
}

export default FaqDs
